import { type ReactElement, useMemo } from 'react';
import type { GeoSpendVendorView } from '@vello/api-client';
import { Badge, EmptyState, Grid, Meter, Notice, PageHeader, Panel, Stack, Table, Text } from '@vello/web-theme';
import { attributionBy, buildCostCard, costFx, freeAllowanceOf, mapsStreams, marketReadableName, microsMoney, percentOfBp, platformCostsAuthority } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { CostBody, useAiSpend, useGeoSpend } from '../components/platform-costs';

/**
 * **Configurações › Gastos de mapas** (D874; the sibling of Gastos de IA), over `GET /v1/config/integrations/geo/spend`.
 *
 * What Google and HERE bill Vello this month, as each bills it: by vendor and SKU (calls, the free allowance as a progress bar, cost), by city and by purpose, the month-end
 * forecast with its range and the same period of last month. Amounts are in the vendor's own currency, with the converted figure (approximate) at the stored rate. There is NO budget
 * here and NO invoice entry (owner, 2026-10-31): the monthly cap `geo.spendCaps` is a governed rule and stays in Regras governadas. The API does not return cache savings yet, so
 * the screen does not show them. A refused read is an error, never zero spend.
 */

function VendorPanel({ vendor }: { vendor: GeoSpendVendorView }): ReactElement {
  const { t, lang } = useT();
  const { v } = useVocab();
  const { markets } = useAdmin();
  const money = (micros: string): string => microsMoney(micros, vendor.currency, lang);
  const sku = (key: string): string => (v('geo.sku', key));
  const cities = attributionBy(vendor, 'marketId');
  const purposes = attributionBy(vendor, 'purpose');
  const unattributed = BigInt(vendor.drift.differenceMicros);
  const projection = vendor.projection;
  const runsOut = projection.lines.filter((l) => l.freeRunsOutAt !== null);
  const approx = vendor.approx;

  return (
    <Panel>
      <Stack gap={4}>
        <Stack gap={1}>
          <h3>{t('mapsspend.vendor.title', { vendor: v('geo.vendor', vendor.vendorId), month: vendor.billingMonth })}</h3>
          <Text as="p" role="label"><span className="tnum">{t('mapsspend.vendor.total', { amount: money(vendor.monthToDate.costMicros) })}</span></Text>
          {approx !== null && <Text as="p" role="caption" tone="secondary"><span className="tnum">{t('mapsspend.vendor.approx', { amount: `≈ ${microsMoney(approx.monthToDateMicros, approx.currency, lang)}` })}</span></Text>}
        </Stack>

        {/* ---- by SKU: calls, the free allowance as progress, cost ---- */}
        <Stack gap={3}>
          <h4>{t('mapsspend.sku.title')}</h4>
          <Text as="p" role="caption" tone="secondary">{t('mapsspend.sku.hint')}</Text>
          {vendor.monthToDate.lines.length === 0 ? <EmptyState title={t('mapsspend.empty')} /> : (
            <Stack gap={4}>
              {vendor.monthToDate.lines.map((line) => {
                const free = freeAllowanceOf(line);
                return (
                  <Stack key={line.skuKey} gap={2}>
                    <Grid min="sm" gap={3}>
                      <Text role="label">
                        {sku(line.skuKey)} {line.unmapped && <Badge tone="warning">{t('mapsspend.unmapped')}</Badge>}
                      </Text>
                      <Text role="caption" tone="secondary"><span className="tnum">{t('mapsspend.col.calls')}: {BigInt(line.units).toLocaleString(lang)}</span></Text>
                      <Text role="caption" tone="secondary"><span className="tnum">{t('mapsspend.col.cost')}: {money(line.costMicros)}</span></Text>
                    </Grid>
                    {free !== undefined ? (
                      <Meter
                        label={t('mapsspend.free.line', { sku: sku(line.skuKey), used: free.used.toLocaleString(lang), free: free.free.toLocaleString(lang) })}
                        value={free.basisPoints}
                        max={10_000}
                        showMax={false}
                        warnAt={0.8}
                        dangerAt={1}
                        formatValue={(x) => percentOfBp(x)}
                      />
                    ) : (
                      <Text role="caption" tone="tertiary">{t('mapsspend.free.none')}</Text>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>

        {/* ---- by city and by purpose ---- */}
        <Grid min="md" gap={4}>
          <Stack gap={2}>
            <h4>{t('mapsspend.city.title')}</h4>
            <Text as="p" role="caption" tone="secondary">{t('mapsspend.city.hint')}</Text>
            <Table
              columns={[
                { key: 'city', header: t('mapsspend.col.city'), render: (r: (typeof cities)[number]) => <span>{marketReadableName(r.key, markets)}</span> },
                { key: 'calls', header: t('mapsspend.col.calls'), numeric: true, render: (r: (typeof cities)[number]) => <span className="tnum">{r.units.toLocaleString(lang)}</span> },
                { key: 'cost', header: t('mapsspend.col.cost'), numeric: true, render: (r: (typeof cities)[number]) => <span className="tnum">{money(r.costMicros.toString())}</span> },
              ]}
              rows={cities}
              rowKey={(r) => r.key}
              empty={<EmptyState title={t('mapsspend.empty')} />}
            />
          </Stack>
          <Stack gap={2}>
            <h4>{t('mapsspend.purpose.title')}</h4>
            <Text as="p" role="caption" tone="secondary">{t('mapsspend.purpose.hint')}</Text>
            <Table
              columns={[
                { key: 'purpose', header: t('mapsspend.col.purpose'), render: (r: (typeof purposes)[number]) => <span>{v('geo.spend_purpose', r.key)}</span> },
                { key: 'calls', header: t('mapsspend.col.calls'), numeric: true, render: (r: (typeof purposes)[number]) => <span className="tnum">{r.units.toLocaleString(lang)}</span> },
                { key: 'cost', header: t('mapsspend.col.cost'), numeric: true, render: (r: (typeof purposes)[number]) => <span className="tnum">{money(r.costMicros.toString())}</span> },
              ]}
              rows={purposes}
              rowKey={(r) => r.key}
              empty={<EmptyState title={t('mapsspend.empty')} />}
            />
          </Stack>
        </Grid>
        {unattributed > 0n && <Text as="p" role="caption" tone="secondary"><span className="tnum">{t('mapsspend.unattributed', { amount: money(unattributed.toString()) })}</span></Text>}

        {/* ---- the forecast of this vendor ---- */}
        <Stack gap={2}>
          <h4>{t('mapsspend.forecast.title')}</h4>
          <Text as="p" role="caption" tone="secondary">
            <span className="tnum">{t('mapsspend.forecast.line', { mid: money(projection.midMicros), low: money(projection.lowMicros), high: money(projection.highMicros), days: projection.daysUsed })}</span>
          </Text>
          {projection.notEnoughHistory && <Text as="p" role="caption" tone="secondary">{t('pcost.forecast.short')}</Text>}
          {runsOut.map((l) => (
            <Text key={l.skuKey} as="p" role="caption" tone="secondary">
              {t('mapsspend.forecast.runsOut', { sku: sku(l.skuKey), date: new Date(l.freeRunsOutAt ?? '').toLocaleDateString(lang, { timeZone: vendor.billingTimeZone }) })}
            </Text>
          ))}
          <Text as="p" role="caption" tone="secondary"><span className="tnum">{t('mapsspend.forecast.same', { amount: money(vendor.samePeriodLastMonth.costMicros) })}</span></Text>
        </Stack>
      </Stack>
    </Panel>
  );
}

export function MapsSpendScreen(): ReactElement {
  const { grants } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const { mayViewMaps, mayViewAi } = platformCostsAuthority(grants);
  const geo = useGeoSpend(mayViewMaps);
  // the AI report only lends its recorded exchange rates, for a vendor currency the maps read has no stored rate for
  const ai = useAiSpend(mayViewAi);

  const summary = useMemo(() => {
    if (geo.status !== 'ready') return undefined;
    const streams = mapsStreams(geo.value);
    return buildCostCard('maps', streams, costFx(geo.value, ai.status === 'ready' ? ai.value.report : undefined, streams.map((s) => s.currency)));
  }, [geo, ai]);

  const header = <PageHeader title={t('mapsspend.title')} subtitle={t('mapsspend.subtitle')} />;
  if (!mayViewMaps) {
    return (
      <>
        {header}
        <Panel><EmptyState title={t('mapsspend.noPermission')} /></Panel>
      </>
    );
  }

  return (
    <>
      {header}
      <Panel><Text as="p" role="caption" tone="secondary">{t('mapsspend.explain')}</Text></Panel>
      {geo.status === 'error' && <Panel><Notice tone="danger">{t('mapsspend.loadFailed', { reason: geo.reason })}</Notice></Panel>}
      {geo.status === 'loading' && <Panel><EmptyState title={t('ui.loading')} /></Panel>}
      {geo.status === 'ready' && (
        <>
          {geo.value.vendors.length === 0 && (
            <Panel>
              {geo.value.priceBook.reasonCode === 'GEO_PRICE_BOOK_UNSET' ? <Notice tone="warning">{t('mapsspend.unset')}</Notice> : <EmptyState title={t('mapsspend.noVendors')} />}
            </Panel>
          )}
          {summary !== undefined && geo.value.vendors.length > 0 && (
            <Panel>
              <Stack gap={3}>
                <h3>{t('pcost.maps.title')}</h3>
                <Text as="p" role="caption" tone="secondary">{t('pcost.maps.subtitle')}</Text>
                <CostBody model={summary} originalLabel={(source) => v('geo.vendor', source)} />
              </Stack>
            </Panel>
          )}
          {geo.value.vendors.map((vendor) => <VendorPanel key={vendor.vendorId} vendor={vendor} />)}
        </>
      )}
    </>
  );
}
